import PropTypes from 'prop-types';
import cx from 'classnames';
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Table } from 'antd';
import { debounce } from 'lodash';
import isWebView from '$src/utils/user-agent';
import { Input } from '@amzn/ring-react-components/csr/components/Input';
import Statistic from '../Statistic';
import { getColumns } from './constants';

import './index.less';

function LocationsTableCard({
  title,
  Icon,
  locations,
  onLocationDetailsClicked,
  'data-testid': dataTestId,
  isLoading,
  setLocationSelected,
  locationIdSelected,
  isSelectionDisabled,
}) {
  const columns = useMemo(() => getColumns(onLocationDetailsClicked), [onLocationDetailsClicked]);
  const [searchedLocation, setSearchedLocation] = useState('');
  const [tableWidth, setTableWidth] = useState();
  const filteredLocations = locations?.filter(
    (l) =>
      l.locationName?.toLowerCase().includes(searchedLocation) ||
      `${l.fullAddress}, ${l.address?.city?.toLowerCase()} `?.toLowerCase().includes(searchedLocation),
  );
  const tableRef = useRef(null);

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      setLocationSelected(selectedRowKeys.length > 1 ? selectedRows[1] : selectedRows[0]);
    },
    hideSelectAll: true,
    selectedRowKeys: locationIdSelected,
    columnWidth: 40,
    getCheckboxProps: () => ({
      disabled: isSelectionDisabled,
    }),
  };

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchedLocation(value.toLowerCase());
    }, 300),
    [],
  );

  const handleSearchChange = (event) => {
    debouncedSearch(event.target.value);
  };

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      setTableWidth(tableRef?.current?.offsetWidth - 200);
    });

    if (tableRef.current) {
      observer.observe(tableRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div data-testid={dataTestId} className="locations-table-card">
      <div className="locations-table-card__header">
        <div>
          <Statistic title={title} value={locations?.length} Icon={Icon} />
        </div>
        <Input
          className={cx('locations-table-card__search', { 'avoid-autofocus': isWebView() })}
          data-testid="search-input"
          placeholder="Search Locations"
          type="text"
          onChange={handleSearchChange}
        />
      </div>
      <div ref={tableRef}>
        <Table
          dataSource={filteredLocations}
          columns={columns}
          virtual
          pagination={false}
          loading={isLoading}
          scroll={{ y: 250, x: tableWidth }}
          rowKey="locationId"
          rowSelection={{
            type: 'checkbox',
            ...rowSelection,
          }}
        />
      </div>
    </div>
  );
}

LocationsTableCard.propTypes = {
  title: PropTypes.string.isRequired,
  Icon: PropTypes.elementType.isRequired,
  locations: PropTypes.array,
  onLocationDetailsClicked: PropTypes.func,
  'data-testid': PropTypes.string,
  isLoading: PropTypes.bool,
  setLocationSelected: PropTypes.func,
  locationIdSelected: PropTypes.array,
  isSelectionDisabled: PropTypes.bool,
};

export default LocationsTableCard;
